export type LegacyLink = { label: string; url: string };
export type LegacyBlockType = 'HERO' | 'MARKDOWN' | 'IMAGE' | 'CTA' | 'LINKS' | 'GALLERY' | 'SPACER';
export type LegacyBlockWidth = 'FULL' | 'HALF' | 'THIRD' | 'TWO_THIRDS';
export type LegacyAlignment = 'LEFT' | 'CENTER' | 'RIGHT';
export type LegacyImage = { url: string; alt: string; link?: string };

export type LegacyBlock = {
  id: string;
  type: LegacyBlockType;
  enabled: boolean;
  width: LegacyBlockWidth;
  alignment: LegacyAlignment;
  adminLabel?: string;
  title?: string;
  content?: string;
  imageUrl?: string;
  imageAlt?: string;
  linkUrl?: string;
  linkLabel?: string;
  backgroundColor?: string;
  textColor?: string;
  links?: LegacyLink[];
  images?: LegacyImage[];
  height?: number;
  animation?: 'none' | 'fade-in' | 'fade-up' | 'fade-left' | 'scale-in';
  animationDelay?: number;
  condition?: string;
  date?: string;
};

export type LegacyTheme = { backgroundColor?: string; surfaceColor?: string; textColor?: string; accentColor?: string };
export type LegacyPageContent = {
  title: string;
  event: string;
  eventDate: string;
  article: string;
  links: LegacyLink[];
  sourceUrl: string;
  theme?: LegacyTheme;
  blocks?: LegacyBlock[];
};

export const blockLabels: Record<LegacyBlockType, string> = {
  HERO: 'Hero', MARKDOWN: 'Markdown', IMAGE: 'Obrázek', CTA: 'Výzva', LINKS: 'Odkazy', GALLERY: 'Galerie', SPACER: 'Mezera'
};

export function createLegacyBlock(type: LegacyBlockType): LegacyBlock {
  return {
    id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
    type,
    enabled: true,
    width: 'FULL',
    alignment: 'LEFT',
    ...(type === 'SPACER' ? { height: 48 } : {}),
    ...(type === 'LINKS' ? { links: [{ label: '', url: '' }] } : {}),
    ...(type === 'GALLERY' ? { images: [{ url: '', alt: '' }] } : {})
  };
}

export function normalizeLegacyContent(input?: Partial<LegacyPageContent>): LegacyPageContent {
  const content: LegacyPageContent = {
    title: input?.title ?? 'NICOVSKÝ NADAČNÍ FOND', event: input?.event ?? '', eventDate: input?.eventDate ?? '',
    article: input?.article ?? '', links: input?.links ?? [], sourceUrl: input?.sourceUrl ?? 'https://nicov.eu',
    theme: input?.theme ?? { backgroundColor: '#efe2c2', surfaceColor: '#f8edcf', textColor: '#38251d', accentColor: '#7b1f2b' }
  };
  if (input?.blocks != null) {
    content.blocks = input.blocks.map((block, i) => ({ ...block, id: block.id || `blk-${i}`, enabled: block.enabled !== false, width: block.width ?? 'FULL', alignment: block.alignment ?? 'LEFT' }));
    return content;
  }
  const eventUrl = content.sourceUrl === 'https://nicov.eu' ? 'https://www.nicov.eu/?article=25122' : content.sourceUrl;
  const blocks: LegacyBlock[] = [
    {
      ...createLegacyBlock('HERO'),
      title: content.title,
      content: [`## ${content.event}`, content.eventDate].filter(Boolean).join('\n\n'),
      imageUrl: 'https://nicov.eu/nicov/images/titulka2023.jpg',
      imageAlt: 'Kostel svatého Martina v Nicově',
      linkUrl: eventUrl,
      linkLabel: 'Více informací',
      alignment: 'CENTER'
    },
    {
      ...createLegacyBlock('LINKS'),
      title: 'Nadační fond',
      width: 'HALF',
      links: content.links.slice(0, 4)
    },
    {
      ...createLegacyBlock('LINKS'),
      title: 'Zprávy a kontakt',
      width: 'HALF',
      links: content.links.slice(4)
    },
    {
      ...createLegacyBlock('CTA'),
      title: 'Objevte Nicov a okolí',
      content: 'Tipy na výlety a zajímavá místa v okolí.',
      linkUrl: 'https://www.kudyznudy.cz/?utm_source=kzn&utm_medium=partneri_kzn&utm_campaign=banner',
      linkLabel: 'Kudy z nudy',
      alignment: 'CENTER'
    },
    {
      ...createLegacyBlock('GALLERY'),
      id: 'nicov-partners',
      title: 'Partneři',
      images: [
        { url: 'https://nicov.eu/nicov/images/pl_kraj_logo.png', alt: 'Plzeňský kraj' },
        { url: 'https://nicov.eu/nicov/images/logo_CS.png', alt: 'Česká spořitelna' },
        { url: 'https://nicov.eu/nicov/images/EU.png', alt: 'Evropská unie' },
        { url: 'https://www.kudyznudy.cz/App_Themes/KzN/CSS/images/svg/logo.svg', alt: 'Kudy z nudy', link: 'https://www.kudyznudy.cz/?utm_source=kzn&utm_medium=partneri_kzn&utm_campaign=banner' }
      ],
      alignment: 'CENTER'
    }
  ];
  if (content.article) blocks.splice(1, 0, { ...createLegacyBlock('MARKDOWN'), content: content.article });
  blocks.push({ ...createLegacyBlock('MARKDOWN'), content: '© [SEVASOFT, s.r.o](http://sevasoft.cz). 2010–2025. All rights reserved. | Powered by [Linux](http://www.linux.cz/)', alignment: 'CENTER' });
  content.blocks = blocks;
  return content;
}

export function validateLegacyContent(input: unknown): { valid: boolean; error?: string } {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { valid: false, error: 'Content must be an object' };
  }
  const typed = input as Partial<LegacyPageContent>;
  if (!Array.isArray(typed.blocks)) {
    return { valid: false, error: 'Missing blocks array' };
  }
  for (let i = 0; i < typed.blocks.length; i++) {
    const res = validateLegacyBlock(typed.blocks[i]);
    if (!res.valid) return { valid: false, error: `block[${i}]: ${res.error}` };
  }
  return { valid: true };
}

export function validateLegacyBlock(input: unknown): { valid: boolean; error?: string } {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { valid: false, error: 'Block must be an object' };
  }
  const typed = input as Partial<LegacyBlock>;
  if (!typed.type || !Object.keys(blockLabels).includes(typed.type)) {
    return { valid: false, error: `Unknown block type: ${typed.type}` };
  }
  if (!typed.id || typeof typed.id !== 'string') {
    return { valid: false, error: 'Block is missing a string id' };
  }
  return { valid: true };
}
